import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CompanyApi } from '@api/companies.api';
import { LocationGroupApi } from '@api/locationGroup.api';
import { LocationsApi } from '@api/locations.api';
import { MaintenanceTypeApi } from '@api/maintenanceType.api';
import { SegmentApi } from '@api/segments.api';
import { AccountMaintenancesAverageDeadlinesItem, DeadlinesWhere, MaintenanceFilter } from '@core/interfaces';
import { splitStringByComma } from '@core/utils/filters-utils';
import { Apollo } from 'apollo-angular';
import gql from 'graphql-tag';
import { chain, isEmpty, sumBy } from 'lodash';
import moment from 'moment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ReportsMapperService } from './reports.mapper.service';

function indexBy(items: any[], property: string): Record<string, any> {
  const itemsMap = {};
  for (const item of items) {
    const key = item[property];
    itemsMap[key] = item;

  }
  return itemsMap;
}

function statusValueByName(statusMap: Record<string, any>, status: string): number {
  const item = statusMap[status];
  return (item && item.value) || 0;
}

@Injectable({
  providedIn: 'root'
})
export class ReportsService {
  constructor(
    private readonly reportsMapperService: ReportsMapperService,
    private readonly route: ActivatedRoute,
    public readonly maintenanceTypeApi: MaintenanceTypeApi,
    public readonly locationGroupApi: LocationGroupApi,
    public readonly companiesApi: CompanyApi,
    public readonly segmentsApi: SegmentApi,
    public readonly locationApi: LocationsApi,
    private readonly apollo: Apollo,
  ) {}

  async transformFilterIdsToObject(filterData): Promise<any> {
    const [
      locations,
      maintenanceTypes,
      segments,
      companies,
      locationGroups
    ] = await Promise.all([
      Promise.all(splitStringByComma(filterData.locationId_in).map((id) => this.locationApi.searchOne(id).toPromise())),
      Promise.all(splitStringByComma(filterData.maintenanceTypeId_in).map((id) => this.maintenanceTypeApi.searchOne(id))),
      Promise.all(splitStringByComma(filterData.segmentId_in).map((id) => this.segmentsApi.searchOne(id))),
      Promise.all(splitStringByComma(filterData.companyId_in).map((id) => this.companiesApi.searchOne(id))),
      Promise.all(splitStringByComma(filterData.locationGroupId_in).map((id) => this.locationGroupApi.searchOne(id)))
    ]);
    const equaledDateParameters = this.reportsMapperService.equalizeFilterQueryParams(filterData);

    return {
      locations: locations.length ? locations : null,
      initialDate: equaledDateParameters.createdAt_gte ? equaledDateParameters.createdAt_gte : null,
      finalDate: equaledDateParameters.createdAt_lte ? equaledDateParameters.createdAt_lte : null,
      maintenanceTypes: maintenanceTypes.length ? maintenanceTypes : null,
      segments: segments.length ? segments : null,
      locationGroups: locationGroups.length ? locationGroups : null,
      companies: companies.length ? companies : null
    };
  }

  public keepOnlyChangedValuesOnQueryParams(currentQueryParams, defaultQueryParams): Object {
    const queryParams = {};

    for (const currentKey of Object.keys(currentQueryParams)) {
      for (const defaultKey of Object.keys(defaultQueryParams)) {
        const isTheSameObjectProperty = currentKey === defaultKey;
        const objectValueHasChanged = currentQueryParams[currentKey] !== defaultQueryParams[defaultKey];

        if (currentQueryParams[currentKey] && (isTheSameObjectProperty && objectValueHasChanged)) {
          queryParams[currentKey] = currentQueryParams[currentKey];
        }
      }
    }
    return queryParams;
  }

  public async preserveSavedFilterValues(localStorageKey, filterForm, defaultFilterValues): Promise<void> {
    const queryParams = this.route.snapshot.queryParams;
    const hasFilteredDataInTheUrl = !isEmpty(queryParams);
    const filterDataSavedInLocalStorage = JSON.parse(localStorage.getItem(localStorageKey));
    const filterData = !hasFilteredDataInTheUrl ? filterDataSavedInLocalStorage : await this.transformFilterIdsToObject(queryParams);

    if (filterData) {
      filterForm.setValue({
        initialDate: filterData.initialDate ? moment(filterData.initialDate) : defaultFilterValues.initialDate,
        finalDate: filterData.finalDate ? moment(filterData.finalDate) : defaultFilterValues.finalDate,
        locations: filterData.locations,
        maintenanceTypes: filterData.maintenanceTypes ? filterData.maintenanceTypes : null,
        segments: filterData.segments ? filterData.segments : null,
        companies: filterData.companies ? filterData.companies : null,
        locationGroups: filterData.locationGroups ? filterData.locationGroups : null
      });
    }

    if (filterForm.valid) {
      return;
    }

    filterForm.setValue(defaultFilterValues);
  }

  public async accountMaintenancesStatus(where: MaintenanceFilter): Promise<any> {
    const query = gql`
      query accountMaintenancesStatus ($where: DashboardInput!) {
        accountMaintenancesStatus(where: $where) {
          reportName
          items {
            name: key
            value
          }
        }
      }
    `;

    return this.apollo
      .query<any>({
        query,
        variables: { where },
        fetchPolicy: 'cache-first'
      })
      .toPromise()
      .then(({ data }) => {
        const statusMap = indexBy(data.accountMaintenancesStatus.items, 'name');
        return {
          totalMaintenances: sumBy(data.accountMaintenancesStatus.items, 'value'),
          status: {
            canceled: statusValueByName(statusMap, 'canceled'),
            done: statusValueByName(statusMap, 'done'),
            inProgress: statusValueByName(statusMap, 'inProgress'),
            new: statusValueByName(statusMap, 'new'),
            pending: statusValueByName(statusMap, 'pending'),
            reported: statusValueByName(statusMap, 'reported'),
            scheduled: statusValueByName(statusMap, 'scheduled'),
            requested: statusValueByName(statusMap, 'requested'),
            onNegotiation: statusValueByName(statusMap, 'onNegotiation')
          },
        };
      });
  }

  async accountMaintenancesStatusInterval(where: MaintenanceFilter): Promise<any> {
    const query = gql`
      query accountMaintenancesStatusInterval ($where: DashboardInput!) {
        accountMaintenancesStatusInterval (where: $where) {
          reportName
          items {
            key
            value
          }
        }
      }`;

    return this.apollo
      .query<any>({
        query,
        variables: { where },
        fetchPolicy: 'cache-first'
      })
      .toPromise()
      .then(({ data }) => {
        return data.accountMaintenancesStatusInterval.items.reduce((accumulator, { key, value }) => {
          accumulator[key] = value;
          return accumulator;
        }, {});
      });
  }

  async accountMaintenancesVolumeCount(where: MaintenanceFilter): Promise<any> {
    const query = gql`
      query accountMaintenancesVolumeCount ($where: DashboardInput!) {
        accountMaintenancesVolumeCount (where: $where) {
          reportName
          items {
            key
            value
          }
        }
      }`;

    return this.apollo
      .query<any>({
        query,
        variables: { where },
        fetchPolicy: 'cache-first'
      })
      .toPromise()
      .then(({ data }) => {
        const totalCount = data.accountMaintenancesVolumeCount.items.find((item) => item.key === 'totalCount').value;
        const acceptedCount = data.accountMaintenancesVolumeCount.items.find((item) => item.key === 'acceptedCount').value;

        return chain(data.accountMaintenancesVolumeCount.items)
          .reduce((acc, curr) => {
            let percent = 0;
            if (totalCount !== 0) {
              if (curr.key === 'acceptedCount' || curr.key === 'refusedCount') {
                percent = parseFloat((curr.value / totalCount * 100).toFixed(1));
              } else if (curr.key === 'completedCount' || curr.key === 'incompleteCount') {
                percent = parseFloat((curr.value / acceptedCount * 100).toFixed(1));
              }
            }
            acc[curr.key] = {
              value: curr.value,
              percent: percent
            };
            return acc;
          }, {})
          .value();
      });
  }

  public getAverageDeadlines(where: DeadlinesWhere): Observable<AccountMaintenancesAverageDeadlinesItem> {
    return this.apollo
      .query({
        query: gql`
          query accountMaintenancesAverageDeadlines($where: DeadlinesWhereInput!) {
            accountMaintenancesAverageDeadlines(where: $where) {
              item {
                maintenanceCount
                closedOnTimeCount
                closedOutTimeCount
                fistVisitOnTimeCount
                fistVisitOutTimeCount
                averageCloseTimeInDays
                closedOnTimePercentage
                responseOnTimePercentage
                averageResponseTimeInDays
                withoutDeadlineCount
              }
            }
          }
        `,
        variables: {
          where: {
            openedAt_lte: where.openedAt_lte,
            openedAt_gte: where.openedAt_gte,
            scheduledAt_gte: where.scheduledAt_gte,
            scheduledAt_lte: where.scheduledAt_lte,
            closedAt_gte: where.closedAt_gte,
            closedAt_lte: where.closedAt_lte,
            maintenanceTypeId_in: where.maintenanceTypeId_in || where.typeId_in,
            locationId_in: where.locationId_in,
            segmentId_in: where.segmentId_in,
            companyId_in: where.companyId_in,
            locationGroupId_in: where.locationGroupId_in
          }
        },
        fetchPolicy: 'cache-first'
      })
      .pipe(map((response) => {
        return response.data['accountMaintenancesAverageDeadlines']['item'] as AccountMaintenancesAverageDeadlinesItem;
      }));
  }

  async getAccountRatingsCount(where): Promise<any> {
    return await this.apollo
      .query<any>({
        query: gql`
          query accountRatingsCountQuery($where: DashboardRatingsWhereInput!) {
            accountRatingsCount(where: $where) {
              reportName
              item {
                totalCount
                positiveCount
                negativeCount
                averageRating
                oneStarCount
                twoStarsCount
                threeStarsCount
                fourStarsCount
                fiveStarsCount
              }
            }
          }
        `,
        variables: {
          where: {
            openedAt_lte: where.openedAt_lte,
            openedAt_gte: where.openedAt_gte,
            scheduledAt_gte: where.scheduledAt_gte,
            scheduledAt_lte: where.scheduled,
            completedAt_gte: where.completedAt_gte || where.closedAt_gte,
            completedAt_lte: where.completedAt_lte || where.closedAt_lte,
            companyId_in: where.companyId_in,
            locationId_in: where.locationId_in,
            segmentId_in: where.segmentId_in,
            locationGroupId_in: where.locationGroupId_in,
            maintenanceTypeId_in: where.maintenanceTypeId_in,
          }
        },
        fetchPolicy: 'cache-first'
      })
      .toPromise()
      .then((result) => result.data.accountRatingsCount.item);
  }
}
